import { Chain } from '@1delta/chain-registry'
import { getEvmClient } from '@1delta/providers'
import { getAddress, isAddress, zeroAddress, type Address } from 'viem'

/**
 * StakedCelo (stCELO) validator-group discovery for the deposit flow.
 *
 * stCELO's `Manager.deposit()` does NOT take a group — it votes per the
 * caller's *current strategy*. The **default** strategy (a first-time
 * depositor's state) currently reverts `Not validator group` on mainnet:
 * its internal active-group list points at groups Celo core no longer
 * recognises after the L2 migration. So a depositor must opt into a
 * specific, currently-valid group via `Manager.changeStrategy(group)`
 * (wired as the `validatorGroup` option on `buildMintStCeloDirect`).
 *
 * This helper returns the groups that are safe to pass there: eligible in
 * the Election, healthy per the StakedCelo `GroupHealth`, and with non-zero
 * receivable-vote capacity (a full group overflows back to the broken
 * default). Sorted by capacity descending — pick the first for the best
 * single-group fit, or let the user choose.
 *
 * Mirrors [`getCoreValidators`](./coreValidators.ts) (Core's analogous
 * "pick a validator to delegate to" requirement).
 */

/** Celo `Registry` system contract (resolves core contract addresses). */
const REGISTRY: Address = '0x000000000000000000000000000000000000ce10'
/** StakedCelo `Manager` proxy (Celo mainnet 42220). */
const STCELO_MANAGER: Address = '0x0239b96d10a434a56cc9e09383077a0490cf9398'

const REGISTRY_ABI = [
  {
    name: 'getAddressForString',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'string', name: 'identifier' }],
    outputs: [{ type: 'address' }],
  },
] as const

const ELECTION_ABI = [
  {
    name: 'getEligibleValidatorGroups',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address[]' }],
  },
] as const

const MANAGER_ABI = [
  {
    name: 'groupHealth',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address' }],
  },
  {
    name: 'getReceivableVotesForGroup',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address', name: 'group' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'getAddressStrategy',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address', name: 'accountAddress' }],
    outputs: [{ type: 'address' }],
  },
] as const

const GROUP_HEALTH_ABI = [
  {
    name: 'isGroupValid',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address', name: 'group' }],
    outputs: [{ type: 'bool' }],
  },
] as const

export interface StCeloValidatorGroup {
  /** Validator-group address (checksummed) to pass as the deposit's
   *  `options.validatorGroup`. */
  group: Address
  /** Receivable-vote capacity, raw CELO (wei) as string. The amount of new
   *  stake this group can still absorb before it overflows. */
  receivableVotes: string
}

/**
 * Fetch the currently-depositable StakedCelo validator groups on-chain,
 * ranked by remaining capacity (descending).
 *
 * Rotates through the configured Celo RPCs (`maxRpcTries`) so one flaky
 * endpoint doesn't fail the read. Throws only if every RPC fails.
 */
export const getStCeloValidatorGroups = async (
  maxRpcTries = 4,
): Promise<StCeloValidatorGroup[]> => {
  let lastErr: unknown
  for (let rpcId = 0; rpcId < maxRpcTries; rpcId++) {
    try {
      const client = getEvmClient(Chain.CELO_MAINNET, rpcId)

      const [electionAddr, groupHealthAddr] = await Promise.all([
        client.readContract({
          address: REGISTRY,
          abi: REGISTRY_ABI,
          functionName: 'getAddressForString',
          args: ['Election'],
        }) as Promise<Address>,
        client.readContract({
          address: STCELO_MANAGER,
          abi: MANAGER_ABI,
          functionName: 'groupHealth',
        }) as Promise<Address>,
      ])

      const eligible = (await client.readContract({
        address: electionAddr,
        abi: ELECTION_ABI,
        functionName: 'getEligibleValidatorGroups',
      })) as readonly Address[]
      if (eligible.length === 0) return []

      const [healthFlags, capacities] = await Promise.all([
        client.multicall({
          allowFailure: true,
          contracts: eligible.map((group) => ({
            address: groupHealthAddr,
            abi: GROUP_HEALTH_ABI,
            functionName: 'isGroupValid',
            args: [group],
          })),
        }),
        client.multicall({
          allowFailure: true,
          contracts: eligible.map((group) => ({
            address: STCELO_MANAGER,
            abi: MANAGER_ABI,
            functionName: 'getReceivableVotesForGroup',
            args: [group],
          })),
        }),
      ])

      return (
        eligible
          .map((group, i) => ({
            group,
            isValid:
              healthFlags[i].status === 'success' &&
              healthFlags[i].result === true,
            receivableVotes:
              capacities[i].status === 'success'
                ? (capacities[i].result as unknown as bigint)
                : 0n,
          }))
          // Only healthy groups with room to absorb the stake — a full or
          // unhealthy group sends the deposit back to the broken default.
          .filter((g) => g.isValid && g.receivableVotes > 0n)
          .sort((a, b) => (b.receivableVotes > a.receivableVotes ? 1 : -1))
          .map((g) => ({
            group: g.group,
            receivableVotes: g.receivableVotes.toString(),
          }))
      )
    } catch (e) {
      lastErr = e
    }
  }
  throw new Error(
    `getStCeloValidatorGroups: all Celo RPCs failed (${String(lastErr)})`,
  )
}

/** StakedCelo `Manager` on Celo mainnet (42220), lowercased. */
export const STCELO_MANAGER_ADDRESS =
  '0x0239b96d10a434a56cc9e09383077a0490cf9398'

/**
 * Decide which validator group (if any) a stCELO deposit should set via
 * `changeStrategy` before `deposit()` — the value to pass as the builder's
 * `options.validatorGroup`. Resolution order:
 *
 *  1. An explicit `requestedGroup` is honoured verbatim (the zero address
 *     means "use the default strategy" → returns `undefined`, no
 *     `changeStrategy`). `changeStrategy` validates eligibility on-chain.
 *  2. Otherwise the caller's current strategy is read: if it is already a
 *     specific group (`getAddressStrategy != 0`, which the Manager keeps
 *     non-zero only while the group stays healthy), the plain `deposit()`
 *     works and `undefined` is returned.
 *  3. If the caller is on the **default** strategy — which currently reverts
 *     `Not validator group` on mainnet — the best-capacity eligible group is
 *     returned so the deposit is routed somewhere valid.
 *
 * Best-effort: returns `undefined` if the chain reads fail, leaving the
 * deposit unchanged rather than throwing.
 */
export const resolveStCeloDepositGroup = async (
  user: Address,
  requestedGroup?: string,
  maxRpcTries = 4,
): Promise<Address | undefined> => {
  if (requestedGroup !== undefined) {
    if (!isAddress(requestedGroup))
      throw new Error(
        `stCELO validatorGroup must be an address, got ${requestedGroup}`,
      )
    return getAddress(requestedGroup) === zeroAddress
      ? undefined
      : getAddress(requestedGroup)
  }

  let lastErr: unknown
  for (let rpcId = 0; rpcId < maxRpcTries; rpcId++) {
    try {
      const client = getEvmClient(Chain.CELO_MAINNET, rpcId)
      const current = (await client.readContract({
        address: STCELO_MANAGER_ADDRESS,
        abi: MANAGER_ABI,
        functionName: 'getAddressStrategy',
        args: [user],
      })) as Address
      // A healthy specific strategy already votes a valid group.
      if (current && getAddress(current) !== zeroAddress) return undefined
      // Default strategy → opt into the best currently-valid group.
      const groups = await getStCeloValidatorGroups(maxRpcTries)
      return groups[0]?.group
    } catch (e) {
      lastErr = e
    }
  }
  void lastErr
  return undefined
}
